import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom';
import Navbar from '../../components/navbar/navbar'
import Footer from '../../components/footer';
import ScrollToTop from '../../components/scroll-to-top';
import { API_BASE, getImageUrl } from '../../utils/api';

interface BlogData {
    _id: string;
    image: string;
    date: string;
    title: string;
    desc: string;
}

export default function Blog() {
    const [blogs, setBlogs] = useState<BlogData[]>([])
    const [loading, setLoading] = useState(true)
    const location = useLocation();
    const categoryParam = new URLSearchParams(location.search).get('category');

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const res = await fetch(`${API_BASE}/blogs`)
                const data = await res.json()
                if (Array.isArray(data)) setBlogs(data)
            } catch (err) {
                console.error(err)
            } finally {
                setLoading(false)
            }
        }
        fetchBlogs()
    }, [])

    return (
        <div className="min-h-screen bg-[#f4f7fa]">
            <Navbar transparent={false} />
            
            {/* Premium Hero Section */}
            <div className="pt-32 pb-16 bg-[#001b4a] relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2"></div>
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-400 rounded-full blur-[100px] translate-x-1/2 translate-y-1/2"></div>
                </div>
                
                <div className="container-fluid mx-auto px-4 md:px-10 lg:px-16 relative z-10">
                    <div className="flex flex-col items-center text-center">
                        <div className="flex items-center gap-2 text-blue-200/60 text-[10px] font-bold uppercase tracking-[0.2em] mb-4">
                            <Link to="/" className="hover:text-white transition-colors">Home</Link>
                            <i className="fa-solid fa-chevron-right text-[8px]"></i>
                            <span className="text-white">Our Articles</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tight">
                            Latest <span className="text-blue-400">Insights</span> & News
                        </h1>
                        <p className="text-blue-100/60 max-w-2xl text-sm md:text-lg font-medium leading-relaxed">
                            Discover the latest trends in real estate, market analysis, and professional tips to help you make the most informed property decisions.
                        </p>
                    </div>
                </div>
            </div>

            <section className="py-20">
                <div className="container-fluid mx-auto px-4 md:px-10 lg:px-16">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-24 bg-white rounded-sm border border-slate-100 shadow-sm">
                            <div className="w-12 h-12 border-4 border-slate-100 border-t-[#001b4a] rounded-full animate-spin mb-6"></div>
                            <p className="text-slate-400 font-bold text-xs tracking-widest uppercase text-center">Curating the finest articles...</p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                                {blogs.filter(blog => {
                                    if (!categoryParam) return true;
                                    // Placeholder categorization logic: match category name in title or description
                                    return blog.title.toLowerCase().includes(categoryParam.toLowerCase()) || 
                                           blog.desc.toLowerCase().includes(categoryParam.toLowerCase());
                                }).map((item: BlogData) => (
                                    <div key={item._id} className="group">
                                         <div className="bg-white rounded-sm border border-slate-100 shadow-sm hover:shadow-2xl transition-all duration-500 overflow-hidden flex flex-col h-full hover:-translate-y-2">
                                            {/* Blog Thumbnail */}
                                            <div className="relative h-64 overflow-hidden shrink-0">
                                                <Link to={`/blog-detail/${item._id}`} className="block h-full">
                                                    <img 
                                                        src={getImageUrl(item.image)} 
                                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                                                        alt={item.title} 
                                                    />
                                                </Link>
                                                 <div className="absolute top-6 left-6 z-10 bg-white/90 backdrop-blur-md text-[#001b4a] px-4 py-1.5 rounded-sm font-black text-[9px] uppercase tracking-widest shadow-lg border border-white/20">
                                                    <i className="fa-regular fa-calendar mr-2"></i>{item.date}
                                                </div>
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                            </div>
                                            
                                            {/* Blog Body */}
                                            <div className="p-8 flex flex-col flex-1">
                                                <h4 className="text-xl font-bold text-black mb-4 group-hover:text-[#001b4a] transition-colors line-clamp-2 leading-snug">
                                                    <Link to={`/blog-detail/${item._id}`}>{item.title}</Link>
                                                </h4>
                                                <div 
                                                    className="text-slate-500 text-sm font-medium mb-8 line-clamp-3 leading-relaxed" 
                                                    dangerouslySetInnerHTML={{ __html: item.desc.substring(0, 150) + (item.desc.length > 150 ? '...' : '') }}
                                                ></div>
                                                
                                                <div className="mt-auto pt-6 border-t border-slate-50 flex items-center justify-between">
                                                    <Link to={`/blog-detail/${item._id}`} 
                                                        className="inline-flex items-center gap-2 text-[#001b4a] hover:text-black text-[11px] font-black uppercase tracking-widest transition-all group-hover:gap-4"
                                                    >
                                                        Continue Reading <i className="fa-solid fa-arrow-right-long"></i>
                                                    </Link>
                                                    <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-300 group-hover:bg-[#001b4a] group-hover:text-white transition-all">
                                                        <i className="fa-solid fa-share-nodes text-[10px]"></i>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                
                                {blogs.length === 0 && (
                                     <div className="col-span-full text-center py-24 bg-white rounded-sm border border-slate-100 shadow-sm flex flex-col items-center">
                                        <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-6 text-slate-200">
                                            <i className="fa-solid fa-newspaper text-3xl"></i>
                                        </div>
                                        <h5 className="text-xl font-bold text-black mb-2 uppercase tracking-wide">Nothing published yet</h5>
                                        <p className="text-slate-400 font-medium max-w-sm mx-auto text-sm leading-relaxed">Our writers are working hard to bring you fresh content. Stay tuned!</p>
                                    </div>
                                )}
                            </div>

                            {/* Pagination matching Project List Style */}
                            {blogs.length > 0 && (
                                <div className="mt-16 flex items-center justify-center gap-2">
                                    <button className="w-10 h-10 flex items-center justify-center rounded-full text-slate-400 border border-slate-200 hover:border-[#001b4a] hover:text-[#001b4a] transition-all">
                                        <i className="fa-solid fa-chevron-left text-xs"></i>
                                    </button>
                                    <button className="w-10 h-10 flex items-center justify-center rounded-full text-xs font-bold bg-[#001b4a] text-white shadow-lg">1</button>
                                    <button className="w-10 h-10 flex items-center justify-center rounded-full text-xs font-bold text-slate-400 border border-slate-200 hover:border-[#001b4a] hover:text-[#001b4a] transition-all">2</button>
                                    <button className="w-10 h-10 flex items-center justify-center rounded-full text-xs font-bold text-slate-400 border border-slate-200 hover:border-[#001b4a] hover:text-[#001b4a] transition-all">3</button>
                                    <button className="w-10 h-10 flex items-center justify-center rounded-full text-slate-400 border border-slate-200 hover:border-[#001b4a] hover:text-[#001b4a] transition-all">
                                        <i className="fa-solid fa-chevron-right text-xs"></i>
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>
            

            <Footer />
            <ScrollToTop />
        </div>
    )
}
